import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { XCircle, XIcon } from 'lucide-react'
import { forwardRef, useState, useRef, KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'

interface KeywordTagsInputProps {
  value: string[]
  onChange: (keywords: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export const KeywordTagsInput = forwardRef<
  HTMLDivElement,
  KeywordTagsInputProps
>(({ value = [], onChange, placeholder, className, disabled }, ref) => {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const addKeyword = (keyword: string, shouldFocus = false) => {
    const trimmed = keyword.trim()
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed])
    }
    setInputValue('')
    if (shouldFocus && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }

  const removeKeyword = (keywordToRemove: string) => {
    onChange(value.filter((k) => k !== keywordToRemove))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addKeyword(inputValue, true)
    } else if (e.key === ';' && inputValue) {
      e.preventDefault()
      addKeyword(inputValue, true)
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeKeyword(value[value.length - 1])
    }
  }

  const handleBlur = () => {
    if (inputValue.trim()) {
      addKeyword(inputValue, false)
    }
  }

  const handleClearAll = () => {
    onChange([])
  }

  return (
    <div
      ref={ref}
      className={cn(
        'flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      {value.length > 0 ? (
        <div className='flex justify-between items-center w-full'>
          <div className='flex flex-wrap items-center'>
            {value.map((keyword) => (
              <Badge
                key={keyword}
                className='m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300 border-foreground/10 text-foreground bg-card hover:bg-card/80'
              >
                {keyword}
                {!disabled && (
                  <XCircle
                    className='ml-2 h-4 w-4 cursor-pointer'
                    onClick={(event) => {
                      event.stopPropagation()
                      removeKeyword(keyword)
                    }}
                  />
                )}
              </Badge>
            ))}
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              disabled={disabled}
              placeholder=''
              className='h-7 flex-1 border-0 bg-transparent p-0 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'
            />
          </div>
          {!disabled && (
            <div className='flex items-center justify-between'>
              <XIcon
                className='h-4 mx-2 cursor-pointer text-muted-foreground'
                onClick={(event) => {
                  event.stopPropagation()
                  handleClearAll()
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className='flex items-center justify-between w-full'>
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            className='h-7 flex-1 border-0 bg-transparent p-0 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 mx-3'
          />
        </div>
      )}
    </div>
  )
})

KeywordTagsInput.displayName = 'KeywordTagsInput'
